import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { TITLES, CONTENTS, type LegalDocumentType } from '../constants/legalDocuments';
import { LegalMarkdown } from './ui/LegalMarkdown';

/**
 * The legal documents, over whatever is already on screen.
 *
 * Onboarding cannot navigate to /legal/:type: leaving /setup discards the
 * half-filled form, and the route was unreachable from there anyway —
 * ProtectedRoute bounces an incomplete profile straight back to setup, which is
 * why tapping Terms during signup appeared to do nothing at all.
 *
 * Reads the same CONTENTS the full-screen route does, so the text a reviewer
 * opens from a public URL and the text a user reads while signing up cannot
 * drift apart.
 */
export function LegalModal({
  type,
  onClose,
}: {
  type: LegalDocumentType;
  onClose: () => void;
}) {
  const title = TITLES[type];
  const content = CONTENTS[type];

  return createPortal(
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center"
      // Above the setup screen, below nothing: this is opened BY a popup-free
      // surface, so it does not join the popup queue.
      style={{ zIndex: 100001, backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="w-full sm:max-w-2xl bg-[#0f0f0f] border border-white/10 rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: '85vh' }}
        onClick={e => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between p-4 border-b border-white/10 shrink-0"
          style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}
        >
          <h2 className="text-lg font-bold text-white">{title}</h2>
          {/* 44x44 so it clears Apple's minimum touch target. */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex items-center justify-center rounded-full text-white/70 hover:text-white"
            style={{ width: 44, height: 44 }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div
          className="overflow-y-auto p-5 space-y-3"
          style={{
            WebkitOverflowScrolling: 'touch',
            paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
          }}
        >
          <LegalMarkdown content={content} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
